/**
 * @file platformApi.js
 * @description Utility module for managing platform data (CRUD operations), trending platforms, categories,
 *              and suggested platforms via authenticated HTTP requests using Axios and Fetch.
 * @created 29-01-2025
 * @author Govind Dindewad
 */

import axios from "axios";
import apiUrl from "./api";

// -------------------- PLATFORM LISTING --------------------

/**
 * Fetches a paginated list of platforms filtered by a search key.
 * @param {string} key - Search keyword for platform names.
 * @param {number} pageNo - Current page number.
 * @returns {Promise<Object|null>} - List of platforms or error.
 */
async function getPlatformList(key, pageNo) {
    const token = localStorage.getItem('token');

    try {
        const response = await fetch(`${apiUrl}secure/platform/retain-all-platform?platformSearch=${key}&pageNumber=${pageNo}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            console.error(`Error: ${response.status} ${response.statusText}`);
            return response;
        }

        const data = await response.json();
        return data.data;
    } catch (error) {
       if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return null;
    }
}

/**
 * Retrieves all available platform categories.
 * @returns {Promise<Object|null>} - List of categories or null if error.
 */
async function getCategoryList() {
    const token = localStorage.getItem('token');

    try {
        const response = await fetch(`${apiUrl}category/retain-all-category`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            console.error(`Error: ${response.status} ${response.statusText}`);
            return response;
        }

        const data = await response.json();
        return data.data;
    } catch (error) {
   if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return null;
    }
}

/**
 * Fetches platforms by a specific category with pagination.
 * @param {string} categoryId - Category identifier.
 * @param {number} pageNumber - Page number for pagination.
 * @returns {Promise<Object|null>} - List of platforms or null if error.
 */
async function getPlatformbyCategory(categoryId, pageNumber) {
    const token = localStorage.getItem('token');

    try {
        const response = await fetch(`${apiUrl}platform/retain-all-trending-platform/by?categoryId=${categoryId}&pageNumber=${pageNumber}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            console.error(`Error: ${response.status} ${response.statusText}`);
            return null;
        }

        const data = await response.json();
        return data.data;
    } catch (error) {
      if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return null;
    }
}

// -------------------- PLATFORM CRUD --------------------

/**
 * Creates a new platform using multipart form data (includes file upload).
 * @param {Object} newPlatform - Platform data including optional image.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function postPlatform(newPlatform) {
    const token = localStorage.getItem('token');
    const file = newPlatform.platformImageFile;
    const fileName = `platform-image-${Date.now()}.png`;

    const formData = new FormData();
    // Append all non-file fields
    Object.entries(newPlatform).forEach(([key, value]) => {
        if (key !== "platformImageFile") {
            formData.append(key, value);
        }
    });

    // Append file if present
    if (file) {
        formData.append("platformImageFile", file, fileName);
    }

    try {
        const response = await axios.post(`${apiUrl}secure/platform/add-platform`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Updates an existing platform (with optional visibility or image updates).
 * @param {Object} newPlatform - Updated platform data.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function updatePlatform(newPlatform) {
    const token = localStorage.getItem('token');
    const file = newPlatform.platformImageFile;
    const fileName = `platform-image-${Date.now()}.png`;
    const formData = new FormData();

    // Iterate over all properties in newPlatform
    Object.entries(newPlatform).forEach(([key, value]) => {
        // Exclude platformImageFile as it's handled separately
        // Exclude updateVisibility if it's not meant to be sent as a direct form field,
        // or handle it explicitly if it needs to be sent.
        if (key !== "platformImageFile") {
            // If updateVisibility is a boolean and should be sent, append it.
            // Otherwise, append all other general properties.
            if (key === "updateVisibility") {
                // If updateVisibility should be sent as part of the form data,
                // ensure it's converted to a string 'true' or 'false'
                // as FormData appends values as strings.
                formData.append(key, String(value));
            } else {
                formData.append(key, value);
            }
        }
    });

    // Handle the platform image file separately
    if (file) {
        formData.append("platformImageFile", file, fileName);
    }

    try {
        const response = await axios.put(`${apiUrl}secure/platform/update-platform`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                // Axios automatically sets 'Content-Type' to 'multipart/form-data'
                // with the correct boundary when you pass a FormData object.
                // Explicitly setting it here is generally not needed and can sometimes cause issues.
                // However, if your backend strictly requires it, keep it.
                // I've left it for now, but often it can be removed.
                'Content-Type': 'multipart/form-data'
            }
        });
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Return the full error response from the server for more details
        return error.response;
    }
}

/**
 * Deletes a platform by its ID.
 * @param {Object} platform - Platform object containing platformId.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function deletePlatform(platform) {
    const token = localStorage.getItem('token');

    try {
        const response = await axios.delete(`${apiUrl}secure/platform/delete-platform?platformId=${platform.platformId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
        return response;
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

// -------------------- TRENDING PLATFORMS --------------------

/**
 * Retrieves all trending platforms.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function getTrendingPlatform() {
    const token = localStorage.getItem('token');
    try {
        return await axios.get(`${apiUrl}platform/retain-all-trending-platform`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Creates a new trending platform.
 * @param {Object} newTrendPlatform - Data including platformId and image.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function postTrendingPlatform(newTrendPlatform) {
    const token = localStorage.getItem('token');
    const file = newTrendPlatform.trendingImageFile;
    const fileName = `platform-image-${Date.now()}.png`;

    const formData = new FormData();
    formData.append("platformId", newTrendPlatform.platformId);
    formData.append("isVisible", newTrendPlatform.isVisible);

    if (file) {
        formData.append("trendingImageFile", file, fileName);
    }

    try {
        return await axios.post(`${apiUrl}secure/platform/add-trending-platform`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (error) {
     if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Updates an existing trending platform.
 * @param {Object} updatedTrendPlatform - Data including platform and file info.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function updateTrendingPlatform(updatedTrendPlatform) {
    const token = localStorage.getItem('token');
    const file = updatedTrendPlatform.trendingImageFile;
    const fileName = `platform-image-${Date.now()}.png`;

    const formData = new FormData();
    formData.append("trendingPlatformId", updatedTrendPlatform.trendingPlatformId);
    formData.append("platformId", updatedTrendPlatform.platformId);

    if (file) {
        formData.append("trendingImageFile", file, fileName);
    }

    try {
        return await axios.put(`${apiUrl}secure/platform/update-trending-platform`, formData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Deletes a trending platform by ID.
 * @param {Object} platform - Platform object containing trendingPlatformId.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function deleteTrendingPlatform(platform) {
    const token = localStorage.getItem('token');

    try {
        return await axios.delete(`${apiUrl}secure/platform/delete-trending-platform?trendingPlatformId=${platform.trendingPlatformId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

// -------------------- SUGGESTED PLATFORMS --------------------

/**
 * Fetches suggested platforms by name (with optional search).
 * @param {string} [searchKey=''] - Platform name or keyword.
 * @param {number} [pageNo=0] - Page number for pagination.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function getSuggestedPlatform(searchKey = '', pageNo = 0) {
    const token = localStorage.getItem('token');
    try {
        return await axios.get(`${apiUrl}secure/platform/retain-all-suggested-platform?suggestedPlatformName=${searchKey}&pageNumber=${pageNo}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

/**
 * Deletes a suggested platform by ID.
 * @param {string} suggestedPlatformId - Unique ID of the suggested platform.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function deleteSuggestedPlatform(suggestedPlatformId) {
    const token = localStorage.getItem('token');

    try {
        return await axios.delete(`${apiUrl}secure/platform/remove-suggestion?suggestedPlatformId=${suggestedPlatformId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

// -------------------- CATEGORY-SPECIFIC UTILITY --------------------

/**
 * Fetches platforms by a category to populate dropdown or mappings.
 * @param {string} categoryId - Encrypted category ID.
 * @returns {Promise<Object>} - Axios response or error.
 */
async function getPlatformsByCategory(categoryId) {
    const token = localStorage.getItem('token');
    try {
        return await axios.get(`${apiUrl}secure/platform/obtain-name-id?categoryId=${categoryId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        return error.response;
    }
}

// Exporting all functions
export {
    getPlatformList,
    getCategoryList,
    getPlatformbyCategory,
    postPlatform,
    updatePlatform,
    deletePlatform,
    getTrendingPlatform,
    postTrendingPlatform,
    updateTrendingPlatform,
    deleteTrendingPlatform,
    getSuggestedPlatform,
    deleteSuggestedPlatform,
    getPlatformsByCategory
};
